/* global debounce */

if (!customElements.get('slide-show')) {
  class SlideShow extends HTMLElement {
    constructor() {
      super();
      this._onLoad = this._onLoad.bind(this);
    }

    connectedCallback() {
      if (this._inited) return;
      // Initialize when the page is fully loaded (ensures images/layout exist)
      if (document.readyState === 'complete') this._onLoad();
      else window.addEventListener('load', this._onLoad, { once: true });
    }

    disconnectedCallback() {
      // Cleanup listeners/timers
      if (!this._inited) return;
      clearTimeout(this.setActiveSlideTimeoutId);
      clearTimeout(this.autoplayTimer);

      if (this.nav && this._onNavClick) this.nav.removeEventListener('click', this._onNavClick);
      if (this.pagination && this._onPaginationClick) this.pagination.removeEventListener('click', this._onPaginationClick);
      if (this.slideshow) {
        if (this._onTouchStart) this.slideshow.removeEventListener('touchstart', this._onTouchStart);
        if (this._onTouchMove) this.slideshow.removeEventListener('touchmove', this._onTouchMove);
        if (this._onWheel) this.slideshow.removeEventListener('wheel', this._onWheel);
        if (this._onTransitionEnd) this.slideshow.removeEventListener('transitionend', this._onTransitionEnd);
      }
    }

    _onLoad() {
      this._inited = true;
      this.init();
    }

    init() {
      this.slideshow   = this.querySelector('.slideshow');
      this.slides      = this.querySelectorAll('.slideshow__slide');
      this.nav         = this.querySelector('.slideshow-nav');
      this.pagination  = this.querySelector('.slideshow-pagination');
      this.rtl         = document.dir === 'rtl';
      this.currentIndex = 0;
      this.swipeThreshold = 80;
      this.wheelTriggerWait = 800;

      if (!this.slideshow || this.slides.length === 0) {
        console.warn('SlideShow: markup missing .slideshow / .slideshow__slide inside', this);
        return;
      }

      // Defaults from data-attributes
      this.dataset.autoplay = this.dataset.autoplay ?? 'false';
      this.autoplayEnabled = this.dataset.autoplay === 'true';
      this.autoplaySpeed = (Number(this.dataset.speed) || 5) * 1000;

      // Nav
      if (this.nav) {
        this.counterCurrent = this.querySelector('.slideshow-nav__counter-current');
        this._onNavClick = this.handleNavClick.bind(this);
        this.nav.addEventListener('click', this._onNavClick);
      }

      // Pagination
      if (this.pagination) {
        this.pageBtns = this.querySelectorAll('.page-btn');
        this._onPaginationClick = this.handlePaginationClick.bind(this);
        this.pagination.addEventListener('click', this._onPaginationClick);
      }

      // Touch
      const hasTouch = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);
      if (hasTouch) {
        this._onTouchStart = this.handleTouchStart.bind(this);
        const fallbackDebounce = (fn, wait) => {
          let t; return (...args) => { clearTimeout(t); t = setTimeout(() => fn.apply(this, args), wait); };
        };
        const deb = typeof debounce === 'function' ? debounce : fallbackDebounce;
        this._onTouchMove = deb(this.handleTouchMove.bind(this), 200);

        this.slideshow.addEventListener('touchstart', this._onTouchStart, { passive: true });
        this.slideshow.addEventListener('touchmove', this._onTouchMove, { passive: true });
      }

      // Wheel (must be passive:false because we call preventDefault)
      this._onWheel = this.handleWheel.bind(this);
      this.slideshow.addEventListener('wheel', this._onWheel, { passive: false });

      // Transitionend
      this._onTransitionEnd = SlideShow.handleTransitionend;
      this.slideshow.addEventListener('transitionend', this._onTransitionEnd);

      this.setSlideVisibility();
      if (this.autoplayEnabled) this.initAutoPlay();
    }

    initAutoPlay() {
      this.addEventListener('mouseover',  this.pauseAutoplay.bind(this));
      this.addEventListener('mouseleave', this.resumeAutoplay.bind(this));
      this.slideshow.addEventListener('focusin',  this.pauseAutoplay.bind(this));
      this.slideshow.addEventListener('focusout', this.resumeAutoplay.bind(this));
      this.addObserver();
      this.startAutoplay();
    }

    handleNavClick(evt) {
    const btn = evt.target.closest('button');
    if (!btn) return;

    if (btn.classList.contains('autoplay-btn')) {
        this.togglePlayState(evt);
        return;
    }

    if (btn.name === 'next') this.showNextSlide();
    else if (btn.name === 'prev') this.showPrevSlide();
    }


    handlePaginationClick(evt) {
        const btn = evt.target.closest('.page-btn');
        if (!btn) return;
        this.autoplayTimeLeft = this.autoplaySpeed;
        this.setActiveSlide(Number(btn.dataset.index));
    }


    handleWheel(evt) {
      if (this.blockWheelTrigger) {
        evt.preventDefault();
      } else if (evt.deltaX > 20) {
        evt.preventDefault();
        this.showNextSlide();
        this.blockWheelTrigger = true;
        setTimeout(() => { this.blockWheelTrigger = false; }, this.wheelTriggerWait);
      } else if (evt.deltaX < -20) {
        evt.preventDefault();
        this.showPrevSlide();
        this.blockWheelTrigger = true;
        setTimeout(() => { this.blockWheelTrigger = false; }, this.wheelTriggerWait);
      }
    }

    handleTouchStart(evt) {
      this.touchStart = evt.changedTouches[0].screenX;
    }

    handleTouchMove(evt) {
      const touchEnd = evt.changedTouches[0].screenX;
      if (touchEnd < this.touchStart - this.swipeThreshold) this.rtl ? this.showPrevSlide() : this.showNextSlide();
      if (touchEnd > this.touchStart + this.swipeThreshold) this.rtl ? this.showNextSlide() : this.showPrevSlide();
    }

    static handleTransitionend(evt) {
      if (!evt.target.matches('.has-motion')) return;
      evt.target.classList.remove('transition-out');
    }

    showNextSlide() {
      let index = this.currentIndex + 1;
      if (index === this.slides.length) index = 0;
      this.setActiveSlide(index, 'next');
    }

    showPrevSlide() {
      let index = this.currentIndex - 1;
      if (index < 0) index = this.slides.length - 1;
      this.setActiveSlide(index, 'prev');
    }

    setActiveSlide(slideIndex, slideDirection) {
      if (slideIndex === this.currentIndex) return;

      clearTimeout(this.setActiveSlideTimeoutId);
      clearTimeout(this.autoplayTimer);

      let dir = slideDirection || (slideIndex < this.currentIndex ? 'prev' : 'next');
      if (this.rtl) dir = dir === 'next' ? 'prev' : 'next';
      this.dataset.direction = dir;

      this.slides[this.currentIndex].classList.add('transition-out');

      this.setActiveSlideTimeoutId = setTimeout(() => {
        this.slideshow.scrollTo({ left: this.slides[slideIndex].offsetLeft, behavior: 'auto' });

        const prev = this.slides[this.currentIndex];
        const next = this.slides[slideIndex];

        next.classList.add('is-active');
        prev.classList.remove('is-active', 'transition-out');

        const transitionedEl = prev.querySelector('.has-motion');
        if (transitionedEl) transitionedEl.classList.add('transition-out');

        if (this.autoplayEnabled) this.autoplayStartTime = Date.now();
        if (this.counterCurrent) this.counterCurrent.textContent = slideIndex + 1;

        this.currentIndex = slideIndex;
        this.setSlideVisibility();
        this.updatePagination();

        if (this.autoplayEnabled && !this.autoplayPaused) {
          this.autoplayTimeLeft = this.autoplaySpeed;
          this.autoplayTimer = setTimeout(this.showNextSlide.bind(this), this.autoplaySpeed);
        }
      }, 200);
    }

    updatePagination() {
      if (!this.pagination || !this.pageBtns) return;
      this.pageBtns.forEach((el, index) => {
        if (index === this.currentIndex) el.setAttribute('aria-current', 'true');
        else el.removeAttribute('aria-current');
      });
    }

    stopAutoplay() {
      this.setSlideshowState('paused');
      clearTimeout(this.autoplayTimer);
    }

    startAutoplay() {
      this.autoplayTimeLeft = null;
      this.autoplayStartTime = Date.now();
      clearTimeout(this.autoplayTimer);
      this.autoplayTimer = setTimeout(this.showNextSlide.bind(this), this.autoplaySpeed);
      this.setSlideshowState('running');
    }

    pauseAutoplay() {
      if (!this.autoplayEnabled || this.autoplayPaused) return;
      this.stopAutoplay();
      if (this.autoplayTimeLeft) {
        this.autoplayTimeLeft -= (Date.now() - this.resumedTime);
      } else {
        this.autoplayTimeLeft = this.autoplaySpeed - (Date.now() - this.autoplayStartTime);
      }
    }

    resumeAutoplay() {
      if (!this.autoplayEnabled || !this.autoplayPaused) return;
      this.resumedTime = Date.now();
      this.autoplayTimer = setTimeout(this.showNextSlide.bind(this), this.autoplayTimeLeft);
      this.setSlideshowState('running');
    }

    addObserver() {
      if (!('IntersectionObserver' in window)) return;
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => entry.isIntersecting ? this.resumeAutoplay() : this.pauseAutoplay());
      });
      observer.observe(this);
    }

    setSlideVisibility() {
      this.slides.forEach((el, index) => {
        const btns = el.querySelectorAll('a');
        const active = index === this.currentIndex;
        el.setAttribute('aria-hidden', active ? 'false' : 'true');
        el.setAttribute('tabindex', active ? '0' : '-1');
        btns.forEach((btn) => active ? btn.removeAttribute('tabindex') : btn.setAttribute('tabindex', '-1'));
      });
    }

    setSlideshowState(state) {
      this.style.setProperty('--play-state', state);
      this.slideshow.setAttribute('aria-live', state === 'running' ? 'off' : 'polite');
      this.autoplayPaused = state === 'paused';
    }

    togglePlayState(evt) {
      evt.target.classList.toggle('is-paused');
      this.autoplayEnabled = !this.autoplayEnabled;
      this.dataset.autoplay = this.autoplayEnabled ? 'true' : 'false';
      this.autoplayEnabled ? this.startAutoplay() : this.stopAutoplay();
    }
  }

  customElements.define('slide-show', SlideShow);
}
